import { useEffect, useRef, useState, useCallback } from "react";

export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || "0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
}

/**
 * Returns a value from -1 to 0 to 1 based on scroll position.
 * 0 = section center is at viewport center (fully converged)
 * -1 = section is below viewport (not yet scrolled to)
 * +1 = section is above viewport (scrolled past)
 */
export function useScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(-1);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const sectionCenter = rect.top + rect.height / 2;
    const viewportCenter = vh / 2;
    // Normalize: 0 when section center is at viewport center
    // Range from about -1 (below) to +1 (above)
    const raw = (viewportCenter - sectionCenter) / (vh * 0.7);
    setProgress(Math.max(-1, Math.min(1, raw)));
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return [ref, progress];
}

export function ScrollReveal({ children, className = "", direction = "up", delay = 0, duration = 800 }) {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  const directionStyles = {
    up: { from: "translateY(60px)", to: "translateY(0)" },
    down: { from: "translateY(-60px)", to: "translateY(0)" },
    left: { from: "translateX(-80px)", to: "translateX(0)" },
    right: { from: "translateX(80px)", to: "translateX(0)" },
    scale: { from: "scale(0.85)", to: "scale(1)" },
    none: { from: "translateY(0)", to: "translateY(0)" },
  };

  const d = directionStyles[direction] || directionStyles.up;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? d.to : d.from,
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export function Marquee({ children, speed = 30, className = "" }) {
  return (
    <div className={`overflow-hidden whitespace-nowrap ${className}`}>
      <div
        className="inline-flex"
        style={{
          animation: `marqueeScroll ${speed}s linear infinite`,
        }}
      >
        {children}
        {children}
      </div>
    </div>
  );
}
